import React from 'react';
import {List, Map} from 'immutable';
import './TriggerTrendView.styl';
import {TriggerActionType} from 'records/TriggerRuleAction';
import {TriggerActionRecipientType} from 'records/TriggerActionRecipient';
import ActionTypeIconWrapper from 'components/triggers/trigger-actions/ActionTypeIconWrapper';
import connectToStores from 'alt-utils/lib/connectToStores';
import TriggerRuleStore from 'stores/triggers/TriggerRuleStore';
import UserStore from 'stores/UserStore';
import TeamListStore from 'stores/TeamListStore';
import UserActions from 'actions/UserActions';
import TeamActions from 'actions/TeamActions';
import AuthStore from 'stores/AuthStore';
import './TriggerNotificationView.styl';
import {InboxIcon} from '@components/icon/InboxIcon';
import {EmailIcon} from '@components/icon/EmailIcon';
import {SlackIcon} from '@components/icon/SlackIcon';

@connectToStores
export default class TriggerNotificationView extends React.Component {
  constructor(props) {
    super(props);
    this.lookupTeamName = this.lookupTeamName.bind(this);
    this.lookupUserName = this.lookupUserName.bind(this);
  }

  static getStores() {
    return [TriggerRuleStore, UserStore, TeamListStore];
  }

  componentDidMount() {
    const isAdmin = AuthStore.getState().user.isAdmin();
    TeamActions.fetchAllTeams.defer(isAdmin);
    UserActions.fetchAll.defer();
  }

  static getPropsFromStores() {
    const users = UserStore.getState().users || new Map();
    const {departmentTeams} = TeamListStore.getState();

    return {
      users,
      departmentTeams,
    };
  }

  getAction(actions, actionType) {
    return actions ? actions.find(a => a.configuration.type === actionType) : null;
  }

  /**
   * creates the damping string
   *
   * @param period - in seconds
   * @return {string} human readable damping for display
   */
  humanDuration(period) {
    const days = period / 3600 / 24;
    if (period === 0) {
      return '0 seconds';
    } else if (period === 1) {
      return `a second`;
    } else if (period === 60) {
      return 'a minute';
    } else if (period === 3600) {
      return 'an hour';
    } else if (period === 3600 * 24) {
      return 'a day';
    } else if (period === 3600 * 24 * 7) {
      return 'a week';
    } else if (period === 3600 * 24 * 30) {
      return 'a month';
    } else if (days % 30 === 0 && days >= 60) {
      return `every ${days / 30} months`;
    } else if (days % 7 === 0 && days >= 14) {
      return `every ${days / 7} weeks`;
    } else if (days >= 2) {
      return `every ${days} days`;
    } else if (period % 3600 === 0 || period > 3 * 3600) {
      return `every ${period / 3600} hour`;
    } else if (period % 60 === 0 || period > 3 * 60) {
      return `every ${period / 60} minutes`;
    } else {
      return `every ${period} seconds`;
    }
  }

  lookupUserName(userId) {
    const user = this.props.users.get(userId);
    return user ? user.getFullName() : userId;
  }

  lookupTeamName(teamId) {
    const teams = Map(
      this.props.departmentTeams
        .valueSeq()
        .toList()
        .flatten(true)
        .filter(t => !!t)
        .map(t => [t.id, t])
    );
    const team = teams.get(teamId);
    return team ? team.name : teamId;
  }

  render() {
    const {rule, trend, actions} = this.props;

    const ruleOwnerId = rule.user_id;
    const folderAction = this.getAction(actions, TriggerActionType.FOLDER);
    const emailAction = this.getAction(actions, TriggerActionType.EMAIL);
    const slackAction = this.getAction(actions, TriggerActionType.SLACK);

    /* prefer to use the email action to present the configuration, the configuration is shared among all actions, but
       since the email one may already have defined recipients, we use that to show the existing list*/

    const recipientsAction =
      emailAction && emailAction.active
        ? emailAction.configuration.emailTo
        : folderAction && folderAction.active
        ? folderAction.configuration.notificationTo
        : new List();
    const toOthers = recipientsAction.find(
      i => i.type === TriggerActionRecipientType.USER && i.userId !== ruleOwnerId
    );
    const userName = toOthers ? this.lookupUserName(toOthers.userId) : '';
    const toTeam = recipientsAction.find(i => i.type === TriggerActionRecipientType.TEAM);
    const teamName = toTeam ? this.lookupTeamName(toTeam.teamId) : '';
    const toManager = recipientsAction.find(i => i.type === TriggerActionRecipientType.MANAGER);
    const toCaller = recipientsAction.find(i => i.type === TriggerActionRecipientType.CALLER);
    const toMe = recipientsAction.find(
      i => i.type === TriggerActionRecipientType.USER && i.userId === ruleOwnerId
    );
    const toShared = recipientsAction.find(i => i.type === TriggerActionRecipientType.SHARED_ME);
    const hasEmailAction = !!emailAction && emailAction.active;
    const hasInboxAction = !!folderAction && folderAction.active;
    const hasSlackAction = !!slackAction && slackAction.active;

    let damping = rule.dampingDelay;
    if (trend && trend.dampingDelay) {
      damping = trend.dampingDelay * 3600 * 24;
    }

    return (
      <div className="trigger-notification-view trigger-container">
        <header>
          <div className="info">
            <div className="title">Notifications</div>
          </div>
        </header>
        <div className="trigger-notification-content trigger-content">
          <h3>Notification Options</h3>
          <div className="notification-options">
            {hasInboxAction && (
              <h4 className="notification-option">
                <ActionTypeIconWrapper enabled>
                  <InboxIcon />
                </ActionTypeIconWrapper>
                <span>Track in Inbox</span>
              </h4>
            )}
            {hasEmailAction && (
              <h4 className="notification-option">
                <ActionTypeIconWrapper enabled>
                  <EmailIcon />
                </ActionTypeIconWrapper>
                <span>Track by Email</span>
              </h4>
            )}
            {hasSlackAction && (
              <h4 className="notification-option">
                <ActionTypeIconWrapper enabled>
                  <SlackIcon />
                </ActionTypeIconWrapper>
                <span>Track via Slack</span>
              </h4>
            )}
          </div>

          {(!!toOthers || !!toTeam || !!toManager || !!toMe || !!toShared) && (
            <div>
              <h3>Notification Settings</h3>
              <div className={'notification-settings'}>
                <h4>Send Notifications to:</h4>
                <ul>
                  {!!toOthers && <li>{userName}</li>}
                  {!!toTeam && <li>Every one in {teamName}</li>}
                  {!!toManager && <li>Callers' manager</li>}
                  {!!toCaller && <li>Caller</li>}
                  {!!toMe && <li>Me</li>}
                  {!!toShared && <li>Anybody this rule is shared with</li>}
                </ul>
                {damping > 0 && (
                  <div>
                    <h4>Notification Throttling</h4>
                    <p className={'notification-throttling'}>
                      Combine into a single notification when this trend matches repeatedly within a
                      time period of {this.humanDuration(damping)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}
